<template>
  <div
    class="relative"
    @mouseenter="pauseAutoplay"
    @mouseleave="resumeAutoplay"
  >
    <div
      ref="trackRef"
      class="categories-track flex gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory px-0.5 pb-1 touch-pan-x select-none"
      :class="{ 'is-dragging': dragActive }"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerLeave"
    >
      <div
        v-for="(category, index) in categories"
        :key="category.id"
        data-category-card
        role="link"
        tabindex="0"
        class="category-card group relative shrink-0 snap-start cursor-pointer"
        :class="[
          cardClass,
          isCircle
            ? ''
            : carouselWidthClass,
          isLabel ? '' : 'overflow-hidden bg-muted',
        ]"
        :style="cardStyle"
        :aria-label="`Categorie ${category.name}`"
        @click="onCardClick(category)"
        @keydown.enter.prevent="onCardClick(category)"
        @keydown.space.prevent="onCardClick(category)"
        @mouseenter="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
        @focus="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
      >
        <template v-if="isLabel">
          <div class="overflow-hidden rounded-2xl bg-muted" :style="imageStyle">
            <AppImage
              v-if="category.image"
              :src="category.image"
              :alt="category.name"
              preset="thumbnail"
              width="300"
              height="225"
              :sizes="imageSizes"
              densities="x1 x2"
              :loading="index === 0 ? 'eager' : 'lazy'"
              :fetchpriority="index === 0 ? 'high' : 'auto'"
              :preload="index === 0"
              draggable="false"
              class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 pointer-events-none"
            />
            <div v-else class="pointer-events-none h-full w-full bg-gradient-to-br from-brand-400 to-brand-600" />
          </div>
          <span
            class="px-0.5 text-base font-semibold text-highlighted transition-colors group-hover:text-brand-600"
            :class="captionAlignClass"
          >
            {{ category.name }}
          </span>
        </template>
        <template v-else>
          <AppImage
            v-if="category.image"
            :src="category.image"
            :alt="category.name"
            preset="thumbnail"
            width="300"
            height="225"
            :sizes="imageSizes"
            densities="x1 x2"
            :loading="index === 0 ? 'eager' : 'lazy'"
            :fetchpriority="index === 0 ? 'high' : 'auto'"
            :preload="index === 0"
            draggable="false"
            class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 pointer-events-none"
          />
          <div v-else class="pointer-events-none h-full w-full bg-gradient-to-br from-brand-400 to-brand-600" />
          <div
            v-if="display.overlayEnabled"
            class="pointer-events-none absolute inset-0 p-4"
            :class="textPositionClass"
            :style="{ backgroundColor: overlayColor }"
          >
            <span class="text-lg font-semibold text-white">{{ category.name }}</span>
          </div>
          <div
            v-else
            class="pointer-events-none absolute inset-0 p-4"
            :class="textPositionClass"
          >
            <span class="text-lg font-semibold text-white drop-shadow-md">{{ category.name }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Category } from '~/types'
import {
  categoryCaptionAlignClass,
  categoryCardClass,
  categoryCardStyle,
  categoryCarouselWidthClass,
  categoryImageSizes,
  categoryImageStyle,
  categoryTextPositionClasses,
  hexToRgba,
  isCircleCategoryShape,
  isLabelCategoryShape,
  type HomepageCategoriesDisplay,
} from '~/utils/homepageCategoriesDisplay'

const props = defineProps<{
  categories: Category[]
  display: HomepageCategoriesDisplay
  intervalMs: number
}>()

const shopPath = useShopPath()
const router = useRouter()
const { prefetchRoute } = useRoutePrefetch()

const trackRef = ref<HTMLElement | null>(null)
const isPaused = ref(false)
const dragActive = ref(false)

let autoplayTimer: ReturnType<typeof setInterval> | null = null
let dragStartX = 0
let dragStartScrollLeft = 0
let dragPointerId: number | null = null
let dragMoved = false
let suppressCardClick = false

const isLabel = computed(() => isLabelCategoryShape(props.display.shape))
const isCircle = computed(() => isCircleCategoryShape(props.display.shape))

const cardClass = computed(() => categoryCardClass(props.display.shape))

const carouselWidthClass = computed(() =>
  categoryCarouselWidthClass(props.display.columnsPerRow),
)

const imageSizes = computed(() =>
  categoryImageSizes(props.display.columnsPerRow),
)

const cardStyle = computed(() =>
  categoryCardStyle(props.display.shape, props.display.height),
)

const imageStyle = computed(() =>
  categoryImageStyle(props.display.shape, props.display.height),
)

const captionAlignClass = computed(() =>
  categoryCaptionAlignClass(props.display.textAlignX),
)

const overlayColor = computed(() =>
  hexToRgba(props.display.overlayColor, props.display.overlayOpacity),
)

const textPositionClass = computed(() =>
  categoryTextPositionClasses(props.display.textAlignX, props.display.textAlignY),
)

function visibleCount(): number {
  if (!import.meta.client) return props.display.columnsPerRow
  return window.matchMedia('(min-width: 1024px)').matches
    ? props.display.columnsPerRow
    : Math.min(2, props.display.columnsPerRow)
}

function prefersReducedMotion(): boolean {
  if (!import.meta.client) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function isFinePointer(): boolean {
  if (!import.meta.client) return true
  return window.matchMedia('(pointer: fine)').matches
}

function getScrollStep(el: HTMLElement): number {
  const card = el.querySelector<HTMLElement>('[data-category-card]')
  if (!card) return el.clientWidth
  const styles = getComputedStyle(el)
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '16') || 16
  return card.offsetWidth + gap
}

function scrollToPosition(left: number, smooth = false) {
  const el = trackRef.value
  if (!el) return

  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
  const target = Math.min(Math.max(left, 0), maxScroll)
  const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto'

  el.scrollTo({ left: target, behavior })
}

function snapToNearest(smooth = true) {
  const el = trackRef.value
  if (!el) return

  const step = getScrollStep(el)
  if (step <= 0) return

  const index = Math.round(el.scrollLeft / step)
  scrollToPosition(index * step, smooth)
}

function scrollNext() {
  const el = trackRef.value
  if (!el || isPaused.value || dragActive.value) return

  const step = getScrollStep(el)
  const maxScroll = el.scrollWidth - el.clientWidth
  if (maxScroll <= 0) return

  const smooth = isFinePointer()
  if (el.scrollLeft >= maxScroll - 2) {
    scrollToPosition(0, smooth)
    return
  }

  scrollToPosition(el.scrollLeft + step, smooth)
}

function startAutoplay() {
  stopAutoplay()
  if (props.categories.length <= visibleCount()) return
  if (prefersReducedMotion()) return

  autoplayTimer = setInterval(scrollNext, props.intervalMs)
}

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer)
    autoplayTimer = null
  }
}

function pauseAutoplay() {
  isPaused.value = true
}

function resumeAutoplay() {
  isPaused.value = false
}

function endDrag(shouldSnap = true) {
  const el = trackRef.value
  if (!el) return

  if (dragPointerId != null && el.hasPointerCapture(dragPointerId)) {
    el.releasePointerCapture(dragPointerId)
  }

  if (dragMoved) {
    suppressCardClick = true
  }

  dragActive.value = false
  dragPointerId = null

  if (shouldSnap && dragMoved) {
    snapToNearest(true)
  }

  dragMoved = false
  resumeAutoplay()
}

function onCardClick(category: Category) {
  if (suppressCardClick) {
    suppressCardClick = false
    return
  }

  void router.push(shopPath(`/categorii/${category.slug}`))
}

function onPointerDown(event: PointerEvent) {
  const el = trackRef.value
  if (!el || event.button !== 0) return

  suppressCardClick = false
  dragActive.value = true
  dragMoved = false
  dragStartX = event.clientX
  dragStartScrollLeft = el.scrollLeft
  dragPointerId = event.pointerId
  pauseAutoplay()

  el.setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  if (!dragActive.value || dragPointerId !== event.pointerId || !trackRef.value) return

  const delta = event.clientX - dragStartX
  if (Math.abs(delta) > 4) {
    dragMoved = true
    event.preventDefault()
  }

  trackRef.value.scrollLeft = dragStartScrollLeft - delta
}

function onPointerUp(event: PointerEvent) {
  if (!dragActive.value || dragPointerId !== event.pointerId) return
  endDrag(true)
}

function onPointerLeave(event: PointerEvent) {
  if (!dragActive.value || dragPointerId !== event.pointerId) return
  if (event.buttons !== 0) return
  endDrag(true)
}

onMounted(() => {
  const el = trackRef.value
  if (el) {
    el.addEventListener('pointermove', onPointerMove, { passive: false })
  }

  startAutoplay()
  if (import.meta.client) {
    window.addEventListener('resize', startAutoplay)
  }
})

onBeforeUnmount(() => {
  const el = trackRef.value
  if (el) {
    el.removeEventListener('pointermove', onPointerMove)
  }

  stopAutoplay()
  if (import.meta.client) {
    window.removeEventListener('resize', startAutoplay)
  }
})

watch(() => [props.categories.length, props.intervalMs], () => {
  startAutoplay()
})
</script>

<style scoped>
.categories-track {
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
}

.categories-track::-webkit-scrollbar {
  display: none;
}

@media (pointer: fine) {
  .categories-track {
    cursor: grab;
  }

  .categories-track.is-dragging {
    cursor: grabbing;
    scroll-snap-type: none;
  }

  .category-card :deep(img) {
    -webkit-user-drag: none;
    user-select: none;
  }
}
</style>
